import type { ContractDateTypeOption } from "@closer/shared";
import { Button, FormField, Input, Modal, Select, Textarea } from "@closer/ui";
import { useEffect, useState } from "react";

/**
 * Kritik tarih ekleme (CLOS-113 `[075]`).
 *
 * ⚠ Tür kataloğu SUNUCUDAN geliyor (`types`); listeyi burada sabitlemek katalog
 * değiştiğinde iki yeri birden güncellemek olurdu.
 *
 * ### ⚠ AD ALANI TÜRE BAĞLI (Mega E2E BUG-024)
 *
 * Katalogdaki `multiple: true` türler KULLANICI TANIMLI kritik tarihlerdir: aynı türden
 * birden çok tarih olabildiği için hangisi olduğunu ancak bir ad ayırt eder ve sunucu o
 * adı ZORUNLU kılıyor (`dates.ts` → "Kritik tarih için bir ad zorunludur"). Diyalogda ad
 * alanı hiç yoktu; canlı ölçümde kullanıcı formu dolduruyor, "Ekle"ye basıyor ve istek
 * 422 ile düşüyordu — üstelik alan hatası hiçbir yere yazılmadığı için ekranda sessizce.
 * Kritik tarih arayüzden EKLENEMİYORDU.
 *
 * ⚠ SİMETRİK KURAL: `multiple: false` türlerde ad KATALOGDAN geliyor ve sunucu gönderilen
 * adı reddediyor ("Bu tarih türünün adı katalogdan gelir"). Bu yüzden alan yalnız gereken
 * türde çiziliyor ve yalnız o türde gövdeye giriyor — her zaman göndermek, ikinci bir 422
 * üretirdi.
 *
 * ⚠ ENGEL ÖNDEN: ad boşken düğme kapalı. Kullanıcıyı kesin bir reddi almaya göndermiyoruz.
 *
 * ⚠ Hassasiyet şimdilik `day`: gövdeye saat dilimi KONMUYOR ve şema bunu ayrıca
 * reddediyor. Gün hassasiyetli bir tarihe saat dilimi iliştirmek, olmayan bir kesinlik
 * iddia etmek olurdu. Saat gerektiren türler geldiğinde hassasiyet seçimi de burada açılır.
 */
export function ContractDateDialog({
  open,
  onClose,
  types,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  types: ContractDateTypeOption[];
  onConfirm: (girdi: Record<string, unknown>) => void;
  isPending: boolean;
}) {
  const [dateType, setDateType] = useState("");
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setDateType(types[0]?.code ?? "");
    setLabel("");
    setValue("");
    setNote("");
  }, [open, types]);

  const secilenTur = types.find((t) => t.code === dateType) ?? null;
  /** ⚠ Katalog konuşuyor, ekran tahmin etmiyor: adı gereken türü `multiple` söylüyor. */
  const adGerekli = secilenTur?.multiple === true;

  function gonder() {
    const govde: Record<string, unknown> = {
      dateType,
      precision: "day",
      value: value.trim(),
      source: "manual",
    };
    /** ⚠ Ad YALNIZ gereken türde gövdeye giriyor; diğerinde sunucu onu reddediyor. */
    if (adGerekli) govde.label = label.trim();
    const notMetni = note.trim();
    if (notMetni.length > 0) govde.note = notMetni;
    onConfirm(govde);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Tarih ekle"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={gonder}
            loading={isPending}
            disabled={dateType === "" || value.trim() === "" || (adGerekli && label.trim() === "")}
          >
            Ekle
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField label="Tarih türü" required>
          <Select
            aria-label="Tarih türü"
            value={dateType}
            onChange={(e) => setDateType(e.target.value)}
          >
            {types.map((t) => (
              <option key={t.code} value={t.code}>
                {t.label}
              </option>
            ))}
          </Select>
        </FormField>

        {/*
          ⚠ Ad alanı yalnız kullanıcı tanımlı türlerde: standart türlerin adı katalogdan
          geliyor ve sunucu gönderilen adı reddediyor.
        */}
        {adGerekli ? (
          <FormField
            label="Ad"
            required
            hint="Bu tarih türünden birden çok tarih olabilir; hangisi olduğunu ad ayırt eder."
          >
            <Input aria-label="Ad" value={label} onChange={(e) => setLabel(e.target.value)} />
          </FormField>
        ) : null}

        <FormField label="Tarih" required>
          <Input
            aria-label="Tarih"
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </FormField>

        <FormField label="Not">
          <Textarea
            aria-label="Not"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FormField>
      </div>
    </Modal>
  );
}
